import React from 'react'

interface TablePaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const getVisiblePages = (currentPage: number, totalPages: number): Array<number | 'ellipsis'> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }

  const pages: Array<number | 'ellipsis'> = [1]
  const startPage = Math.max(2, currentPage - 1)
  const endPage = Math.min(totalPages - 1, currentPage + 1)

  if (startPage > 2) {
    pages.push('ellipsis')
  }

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page)
  }

  if (endPage < totalPages - 1) {
    pages.push('ellipsis')
  }

  pages.push(totalPages)
  return pages
}

const pageButtonClass = (isActive: boolean): string => {
  if (isActive) {
    return 'size-8 bg-Estigroup-primary rounded-md flex justify-center items-center overflow-hidden text-buttons-text-primary text-xs font-semibold font-Montserrat'
  }

  return 'size-8 bg-transparent rounded-md flex justify-center items-center text-buttons-text-secondary text-xs font-semibold font-Montserrat hover:bg-lightGrey'
}

const TablePagination = ({ currentPage, totalPages, onPageChange }: TablePaginationProps): JSX.Element => {
  if (totalPages === 0) {
    return <div />
  }

  const pages = getVisiblePages(currentPage, totalPages)

  return (
    <div className="self-stretch px-4 py-3 bg-backgrounds-bg-field-component border-t border-borders-border-base inline-flex justify-center items-center gap-2">
      <div className="flex justify-center items-center gap-1">
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => {
            onPageChange(currentPage - 1)
          }}
          className={`size-8 rounded-md flex justify-center items-center text-buttons-text-secondary text-lg font-semibold font-Montserrat ${
            currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-lightGrey'
          }`}>
          ‹
        </button>
        {pages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <div
                key={`ellipsis-${index}`}
                className="size-8 rounded-md flex justify-center items-center text-buttons-text-secondary text-xs font-semibold font-Montserrat">
                …
              </div>
            )
          }

          return (
            <button
              type="button"
              key={page}
              onClick={() => {
                onPageChange(page)
              }}
              className={pageButtonClass(currentPage === page)}>
              {page}
            </button>
          )
        })}
        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => {
            onPageChange(currentPage + 1)
          }}
          className={`size-8 rounded-md flex justify-center items-center text-buttons-text-secondary text-lg font-semibold font-Montserrat ${
            currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : 'hover:bg-lightGrey'
          }`}>
          ›
        </button>
      </div>
    </div>
  )
}

export default TablePagination
